"use client";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { t } from "i18next";
import { Check, ChevronsUpDown, Loader, Search } from "lucide-react";
import * as React from "react";
import { useDebounce } from "use-debounce";

interface ComboBoxOption {
  value: string;
  label: string;
}

interface ComboBoxProps {
  fetchOptions: (query: string) => Promise<ComboBoxOption[]>;
  value: string;
  onChange: (value) => void;
  placeholder?: string;
  label?: string;
  helperText?: string;
  disabled?: boolean;
  queryKeyExtra?: any[];
  isEditing?: boolean;
  returnFullObject?: boolean;
  maxHeight?: string | number;
}

export function ComboBox({
  fetchOptions,
  value,
  onChange,
  helperText,
  placeholder = t("common:selectAnOption"),
  disabled,
  label,
  queryKeyExtra,
  isEditing,
  returnFullObject,
  maxHeight = "200px",
}: ComboBoxProps) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const { data: options = [], isFetching } = useQuery({
    queryKey: [`combo-options`, debouncedSearchQuery, ...(queryKeyExtra || [])],
    queryFn: () => fetchOptions(debouncedSearchQuery),
    enabled: open || isEditing,
  });

  const displayValue = React.useMemo(() => {
    return options.find((option) => option.value === value)?.label || "";
  }, [value, options]);

  const sortedOptions = React.useMemo(() => {
    if (!value)
      return [...options].sort((a, b) => a.label.localeCompare(b.label));

    const selected = options.find((o) => o.value === value);
    const rest = options
      .filter((o) => o.value !== value)
      .sort((a, b) => a.label.localeCompare(b.label));

    return selected ? [selected, ...rest] : rest;
  }, [options, value]);

  const handleOptionSelect = (optionValue: string | ComboBoxOption) => {
    if (typeof optionValue === "string") {
      onChange(optionValue === value ? "" : optionValue);
    } else if (returnFullObject) {
      onChange(optionValue.value === value ? "" : optionValue);
    }
    setOpen(false);
    setSearchQuery("");
  };

  const [popoverWidth, setPopoverWidth] = React.useState<string>("auto");

  React.useEffect(() => {
    if (open && triggerRef.current) {
      const triggerWidth = triggerRef.current.offsetWidth;
      const viewportWidth = window.innerWidth;

      const idealWidth = Math.max(triggerWidth, 200);
      const maxAllowedWidth = viewportWidth * 0.9;

      setPopoverWidth(`${Math.min(idealWidth, maxAllowedWidth)}px`);
    }
  }, [open]);

  React.useEffect(() => {
    if (scrollRef.current && options.length > 0) {
      scrollRef.current.scrollTop = scrollRef.current.scrollTop;
    }
  }, [options]);

  return (
    <div className="flex flex-col gap-2 w-full">
      {label && <Label>{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={triggerRef}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={`bg-[#09090b] flex justify-between disabled:bg-bgDisabled text-muted !text-sm ${
              helperText ? "!border-error" : "border-colorBorder"
            }`}
          >
            <p className="line-clamp-1 text-left flex-1">
              {displayValue || placeholder}
            </p>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0"
          align="start"
          side="bottom"
          sideOffset={4}
          collisionPadding={10}
          style={{
            width: popoverWidth,
            minWidth: "200px",
            maxWidth: "90vw",
            transform: "translateZ(0)",
            isolation: "isolate",
          }}
        >
          <div className="flex flex-col border border-colorBorder rounded-md bg-popover text-popover-foreground">
            <div className="flex items-center px-3 border-b border-colorBorder">
              <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
              <input
                placeholder={`${placeholder}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>

            <div
              ref={scrollRef}
              className="scroll-container"
              style={{
                minHeight: "auto",
                maxHeight:
                  typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight,
                overflowY: "scroll",
                overflowX: "hidden",
                position: "relative",
                isolation: "isolate",
                contain: "layout style",
                scrollBehavior: "smooth",
                scrollbarWidth: "thin",
                scrollbarColor: "#888 #f1f1f1",
              }}
              onWheel={(e) => {
                e.stopPropagation();
              }}
            >
              {isFetching ? (
                <div className="h-full flex items-center justify-center py-8">
                  <Loader className="w-4 h-4 animate-spin" />
                </div>
              ) : options.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-muted-foreground py-8">
                  {t("common:emptyOption")}
                </div>
              ) : (
                <div className="p-1">
                  {sortedOptions.map((option, index) => (
                    <div
                      key={option.value}
                      onClick={() => handleOptionSelect(returnFullObject ? option : option.value)}
                      className={cn(
                        "relative flex cursor-pointer gap-2 select-none items-center rounded-sm px-2 py-2 text-sm outline-none transition-colors",
                        "hover:bg-accent hover:text-accent-foreground",
                        "active:bg-accent active:text-accent-foreground",
                        "min-h-[36px] flex-shrink-0",
                        "sm:py-1.5 sm:min-h-[32px]"
                      )}
                      role="option"
                      aria-selected={value === option.value}
                      data-index={index}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4 flex-shrink-0",
                          value === option.value ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <span
                        className="flex-1 text-left break-words"
                        title={option.label}
                      >
                        {option.label}
                      </span>
                    </div>
                  ))}
                  <div className="h-2" />
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {helperText && <p className="text-sm text-error">{helperText}</p>}
    </div>
  );
}
